import { useEffect, useMemo, useState } from "react";
import { getAdminCategories, rebuildGallery, updateGalleryProject } from "../../lib/api/projectGallery";
import type { GalleryCategory, GalleryProject } from "../../types/projectGallery";

export default function GalleryAdminPage() {
  const [categories, setCategories] = useState<GalleryCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [editing, setEditing] = useState<GalleryProject | null>(null);
  const [form, setForm] = useState({
    project_name: "",
    project_emoji: "",
    category_id: 0,
  });

  async function loadData() {
    setLoading(true);
    try {
      const data = await getAdminCategories();
      setCategories(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  const flatProjects = useMemo(
    () => categories.flatMap((cat) => cat.projects),
    [categories]
  );

  async function handleRebuild() {
    const ok = window.confirm("这会调用 AI 并覆盖当前分类结果，继续吗？");
    if (!ok) return;

    setRebuilding(true);
    try {
      const result = await rebuildGallery();
      alert(`重建完成：${result.total_categories} 个分类，${result.total_projects} 个项目`);
      await loadData();
    } catch (error: any) {
      alert(error?.response?.data?.detail || "重建失败");
    } finally {
      setRebuilding(false);
    }
  }

  function openEditor(project: GalleryProject) {
    setEditing(project);
    setForm({
      project_name: project.project_name,
      project_emoji: project.project_emoji,
      category_id: project.category_id,
    });
  }

  async function handleSave() {
    if (!editing) return;

    try {
      await updateGalleryProject(editing.version_id, form);
      alert("保存成功");
      setEditing(null);
      await loadData();
    } catch (error: any) {
      alert(error?.response?.data?.detail || "保存失败");
    }
  }

  if (loading) {
    return <div style={{ padding: 24 }}>加载中...</div>;
  }

  return (
    <div style={{ padding: 24, color: "#fff", background: "#0b1020", minHeight: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0 }}>项目作品库管理</h1>
          <p style={{ opacity: 0.7 }}>共 {categories.length} 个分类，{flatProjects.length} 个项目</p>
        </div>
        <button
          onClick={handleRebuild}
          disabled={rebuilding}
          style={{
            padding: "12px 18px",
            borderRadius: 12,
            border: "none",
            cursor: "pointer",
            fontWeight: 700,
          }}
        >
          {rebuilding ? "AI 重建中..." : "AI 重新归类"}
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16 }}>
        {categories.map((cat) => (
          <div
            key={cat.id}
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 18,
              padding: 16,
            }}
          >
            <h2 style={{ marginTop: 0 }}>
              {cat.category_emoji || "🪐"} {cat.category_name}
            </h2>

            {cat.projects.length === 0 ? (
              <div style={{ opacity: 0.6 }}>暂无项目</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {cat.projects.map((project) => (
                  <div
                    key={project.version_id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: 12,
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.05)",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700 }}>
                        {project.project_emoji} {project.project_name}
                      </div>
                      <div style={{ opacity: 0.7, fontSize: 13, marginTop: 4 }}>
                        {project.first_message_content || "无首条提示词"}
                      </div>
                      <a
                        href={project.oss_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 13, color: "#8cc8ff" }}
                      >
                        打开作品
                      </a>
                    </div>

                    <button
                      onClick={() => openEditor(project)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: "1px solid rgba(255,255,255,0.2)",
                        background: "transparent",
                        color: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      编辑
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 520,
              background: "#12182b",
              borderRadius: 20,
              padding: 24,
            }}
          >
            <h3 style={{ marginTop: 0 }}>编辑项目</h3>

            <div style={{ display: "grid", gap: 14 }}>
              <label>
                <div style={{ marginBottom: 6 }}>项目名</div>
                <input
                  value={form.project_name}
                  onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                  style={{ width: "100%", padding: 10, borderRadius: 10 }}
                />
              </label>

              <label>
                <div style={{ marginBottom: 6 }}>Emoji</div>
                <input
                  value={form.project_emoji}
                  onChange={(e) => setForm({ ...form, project_emoji: e.target.value })}
                  style={{ width: "100%", padding: 10, borderRadius: 10 }}
                />
              </label>

              <label>
                <div style={{ marginBottom: 6 }}>分类</div>
                <select
                  value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: Number(e.target.value) })}
                  style={{ width: "100%", padding: 10, borderRadius: 10 }}
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button onClick={() => setEditing(null)} style={{ padding: "10px 14px", borderRadius: 10 }}>
                取消
              </button>
              <button onClick={handleSave} style={{ padding: "10px 14px", borderRadius: 10 }}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}